import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteModal = ({ service, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    plotSize: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypeOptions = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'renovation', label: 'Renovation' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Thank you! We will contact you soon with a quote.');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        projectSize: '',
        timeline: '',
        budget: '',
        message: ''
      });
    }, 2000);
  };

  const handleWhatsAppContact = () => {
    const message = `Hi, I'm interested in ${service.name} services. Can you please provide a quote?`;
    const whatsappUrl = `https://wa.me/918003989393?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">Get Quote</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {service.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* WhatsApp Contact Option */}
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Icon name="MessageCircle" size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-800">Quick Contact</h3>
                <p className="text-sm text-green-600">Get instant response via WhatsApp</p>
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              fullWidth
              className="mt-3 bg-green-500 hover:bg-green-600"
              onClick={handleWhatsAppContact}
            >
              <Icon name="MessageCircle" size={16} className="mr-2" />
              Contact on WhatsApp
            </Button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or fill the form below</span>
            </div>
          </div>

          {/* Quote Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Full Name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                required
              />
              <Input
                label="Phone Number"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(value) => handleInputChange('phone', value)}
                required
              />
            </div>

            <Input
              label="Email Address"
              placeholder="Enter your email"
              type="email"
              value={formData.email}
              onChange={(value) => handleInputChange('email', value)}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Project Type"
                placeholder="Select project type"
                options={projectTypeOptions}
                value={formData.projectType}
                onChange={(value) => handleInputChange('projectType', value)}
                required
              />
              <Input
                label="Plot Size"
                placeholder="e.g., 2000 sq ft"
                value={formData.plotSize}
                onChange={(value) => handleInputChange('plotSize', value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Details
              </label>
              <textarea
                placeholder="Tell us more about your project requirements..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={4}
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Quote Request'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuoteModal; 